import { createElement, useEffect, useRef, useState } from "react";
import { Trans } from "react-i18next";
import { TaskLaneStateEnum, TaskRawStateEnum, TaskStateEnum } from "../annualWheel/AnnualWheel.types";

export const taskManagementAllMyStorageKey = "tasksOnlyUserSpecific";
import { useUserContext } from "../auth/userContextProvider/UserContextProvider";
import { ajaxQueue } from "../common/api/ajaxQueue";
import { put } from "../common/api/apiShared";

import { useDotLegalSnackbar, useStateLocalStorage } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../localization/useTranslation";
import { OverviewTaskViewModel, TaskManagementSearchResultViewModel } from "./TaskManagement.types";
import { isApprovalRequired } from "../annualWheel/ApprovalOptions";
import { useOverdueTasksContext } from "../common/providers/overdueTasksProvider/OverdueTasksProvider.tsx";
import { useSearchParams } from "react-router";
import { useDotLegalMutation, useDotLegalPostGetQueryArray, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";

import { useRefetchWhenPaneSaves } from "../main/panes/usePanes";
import { useTaskUrlBinding } from "./useTaskUrlBinding.ts";
import { useTaskQuery } from "./taskPane/TaskPaneContent.hooks.ts";
import { isNullOrWhitespace } from "../common/stringOperations.ts";
import { usePageView } from "../common/pageView/PageView.hooks.ts";
import { FilterPage, SessionStorageSavedView } from "../common/pageView/PageView.types.ts";
import { useSavedViewQuery } from "@common/components/savedViews/SavedViewsMenu.hooks.ts";
import { SavedView } from "@common/components/savedViews/SavedViewsMenu.types.ts";
import { TaskFilterRequest, TaskTableRequest, transformTaskFilterOptionsToBackend } from "./TaskFilter.types.ts";
import { FilterOption } from "../common/components/filterPopover/FilterPopover.types.ts";
import { withoutFilterPresentationState } from "@common/components/filterBar/filterPinning.ts";
import { withFilterOperators } from "@common/components/filterBar/filterOperatorHydration.ts";
import { ListFilterOperator } from "@common/api/filterTypes.ts";

export function useTaskManagementDataMapping() {
    const snackbar = useDotLegalSnackbar();
    const { permissions } = useUserContext();
    const { translateString } = useTranslation();
    const { refetch: refetchOverdueTasksForNavMenu } = useOverdueTasksContext();

    const [year, setYear] = useStateLocalStorage("annualWheelYear", new Date().getFullYear());
    const [searchString, setSearchString] = useState("");

    const [searchParams, setSearchParams] = useSearchParams();
    const cameFromEmail = searchParams.get("fromEmail") === "true";
    const openedTaskId = useTaskUrlBinding();

    // The board is filtered by year, so a task linked from an email is invisible behind its pane until the
    // year follows it. Only the task the url named may move it; stepping to a sibling inside the pane is a
    // side trip that leaves the board alone.
    const { data: openedTask } = useTaskQuery(openedTaskId, { enabled: !isNullOrWhitespace(openedTaskId), throwOnError: false });

    const savedViewQuery = useSavedViewQuery(FilterPage.Task);
    const {
        currentViewId,
        getFilterData,
        isSavedView,
        setFilterData,
        clearFilterData,
        hasFilterChanged,
        setView,
        resetToDefaultView,
        pinnedFilterIds,
        setPinnedFilterIds,
    } = usePageView<TaskFilterRequest>(FilterPage.Task, savedViewQuery.data);

    const defaultTaskFilter: TaskFilterRequest = {
        showOnlyMine: false,
    };
    const filterData = getFilterData<TaskFilterRequest>() ?? defaultTaskFilter;
    // filterData keeps the pins and the filter operators so saving a view captures them; everything that talks to the server uses
    // this one, so pinning an already-empty filter, or changing its operator, does not change the query key and refetch the list.
    const queryFilterData = withoutFilterPresentationState(filterData);

    useEffect(() => {
        const urlMonths = searchParams.get("months");
        const urlTask = searchParams.get("task");
        const urlOverdue = searchParams.get("overdueTasks");
        const urlApproachingAndOverdue = searchParams.get("approachingDeadlineAndOverdueTasks");
        const urlTaskTypes = searchParams.get("taskType");

        if (urlMonths === null && !urlTask && !urlOverdue && !urlApproachingAndOverdue && !urlTaskTypes) return;

        const urlFilter: TaskFilterRequest = { showOnlyMine: false };

        if (urlMonths) {
            const monthValues = urlMonths.split(",").filter(Boolean).map(Number);
            if (monthValues.length > 0) {
                urlFilter.months = { value: monthValues, filterOperator: ListFilterOperator.In };
            }
        }

        if (urlTask) {
            urlFilter.annualWheelActivityIds = { value: [urlTask], filterOperator: ListFilterOperator.In };
        }

        if (urlOverdue === "true") {
            urlFilter.overdueTasks = true;
        }

        if (urlApproachingAndOverdue === "true") {
            urlFilter.approachingDeadlineAndOverdueTasks = true;
        }

        if (urlTaskTypes) {
            urlFilter.taskType = { value: urlTaskTypes.split(",").filter(Boolean), filterOperator: ListFilterOperator.In };
        }

        setFilterData(urlFilter);

        const newParams = new URLSearchParams(searchParams);
        newParams.delete("months");
        newParams.delete("task");
        newParams.delete("overdueTasks");
        newParams.delete("approachingDeadlineAndOverdueTasks");
        newParams.delete("taskType");
        setSearchParams(newParams, { replace: true });
        // Every arrival, not only the first: a link pressed from a pane that is open over this page does not
        // remount it, so a run-once effect would take the parameters and do nothing with them. Stripping the
        // parameters at the end is what keeps this from applying the same filter twice.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    // Opened from an email deep-link: reset the remembered filter to the user's default view so the
    // emailed task is visible on the board behind the dialog (the default view is "approaching and
    // overdue", which matches the tasks emails notify about). Wait for the saved views to load, then
    // strip the marker to keep the effect idempotent.
    useEffect(() => {
        if (!cameFromEmail || !savedViewQuery.data) return;

        resetToDefaultView();

        const newParams = new URLSearchParams(searchParams);
        newParams.delete("fromEmail");
        setSearchParams(newParams, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cameFromEmail, savedViewQuery.data]);

    const searchUrl = "/taskManagement/search";
    const requestBody: TaskTableRequest = {
        filter: { ...queryFilterData, searchText: searchString || undefined },
        year: year!,
    };

    const queryKey = [searchUrl, JSON.stringify(queryFilterData), searchString, year];

    const { data, isLoading, refetch } = useDotLegalPostGetQueryArray<TaskManagementSearchResultViewModel>(queryKey, searchUrl, requestBody);

    const { data: previousYearsHaveOverdueTasks } = useDotLegalQuery<boolean>(`/taskManagement/previousYearsHaveOverdueTasks?year=${year}`);

    const updateTaskStateMutation = useDotLegalMutation(updateTaskState, {
        onSuccess: async () => {
            await refetch();
            refetchOverdueTasksForNavMenu();
            isDragging.current = false;
        },
    });

    function refetchTasks() {
        refetch();
    }

    const setYearByTaskYear = (taskYear: number) => {
        if (year !== taskYear && new Date().getFullYear() <= taskYear) {
            setYear(taskYear);
        }
    };

    useEffect(() => {
        if (openedTask?.deadline) {
            setYearByTaskYear(new Date(openedTask.deadline).getFullYear());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openedTask?.deadline]);

    // The pane covers the board while it is open and has no onSave prop to call, so a saved task reaches
    // back through the panel instead of through the dialog's onDialogClose.
    useRefetchWhenPaneSaves(refetch);

    const overdueTaskCount = data?.overdueTaskCount ?? 0;
    const inProgressTaskCount = data?.inProgressTaskCount ?? 0;
    const completedTaskCount = data?.completedTaskCount ?? 0;

    const [localTasks, setLocalTasks] = useState<Array<OverviewTaskViewModel>>([]);
    const isDragging = useRef(false);

    useEffect(() => {
        if (!isDragging.current && data?.tasks) {
            setLocalTasks(data.tasks);
        }
    }, [data?.tasks]);

    const ready = localTasks.filter((x) => x.laneState === TaskLaneStateEnum.Ready);
    const inProgress = localTasks.filter((x) => x.laneState === TaskLaneStateEnum.InProgress);
    const completed = localTasks.filter((x) => x.laneState === TaskLaneStateEnum.Completed);

    function changeTaskState(sourceId: string, sourceColumn: number, destinationId: string, destinationColumn: number) {
        isDragging.current = true;
        const newTasks = [...localTasks];

        if (sourceColumn === destinationColumn) {
            reOrderTasks(newTasks, sourceId, destinationId);
        } else {
            move(newTasks, sourceId, sourceColumn, destinationId, destinationColumn);
        }

        setLocalTasks(newTasks);

        ajaxQueue().addRequest(updateTaskStateMutation.mutateAsync, { fromTaskId: sourceId, toTaskId: destinationId, taskState: destinationColumn });
    }

    function reOrderTasks(tasks: Array<OverviewTaskViewModel>, sourceId: string, destinationId: string) {
        const sourceIndex = tasks.findIndex((x) => x.id === sourceId);
        const destinationIndex = tasks.findIndex((x) => x.id === destinationId);
        const [fromTask] = tasks.splice(sourceIndex, 1);
        tasks.splice(destinationIndex, 0, fromTask);
    }

    async function failedToChangeTaskState(sourceId: string) {
        const task = localTasks.find((x) => x.id === sourceId);

        if (task !== undefined) {
            if (task.documentationRequired && !task.hasDocuments) {
                snackbar.show(translateString("documentataionRequiredForTask"), "error");
                return;
            }

            if (isApprovalRequired(task.approval)) {
                snackbar.show(createElement(Trans, { i18nKey: "approvalRequiredByResponsibleBeforeCompletion", values: { taskName: task.name } }), "error");
                return;
            }

            if (task.isProcessingActivityValidation) {
                snackbar.show(createElement(Trans, { i18nKey: "processingActivityTaskValidationFailed" }), "error");
            } else {
                snackbar.show(translateString("generelTaskUpdateFailed"), "error");
            }
        }
    }

    function move(tasks: Array<OverviewTaskViewModel>, sourceId: string, sourceColumn: number, destinationId: string, destinationColumn: number) {
        const sourceIndex = tasks.findIndex((x) => x.id === sourceId);
        const destinationTasks = tasks.filter((x) => x.laneState === destinationColumn);
        let destinationIndex = destinationTasks.findIndex((x) => x.id === destinationId);

        if (destinationIndex === -1) {
            destinationIndex = tasks.length;
        } else {
            destinationIndex = tasks.indexOf(destinationTasks[destinationIndex]);
        }

        const [sourceTask] = tasks.splice(sourceIndex, 1);
        sourceTask.laneState = destinationColumn;
        sourceTask.isOverdue = getState(sourceTask.deadline, destinationColumn) === TaskStateEnum.Overdue;
        tasks.splice(destinationIndex > sourceIndex ? destinationIndex - 1 : destinationIndex, 0, sourceTask);
    }

    function getState(taskDeadline: Date, destinationState: TaskLaneStateEnum): TaskStateEnum | TaskLaneStateEnum {
        if (taskDeadline === null) return destinationState;
        if (destinationState === TaskLaneStateEnum.Completed) return TaskStateEnum.Completed;

        const now = new Date();
        const deadline = new Date(taskDeadline);
        return now.getTime() > deadline.getTime() ? TaskStateEnum.Overdue : destinationState;
    }

    async function updateTaskState(data: { fromTaskId: string; toTaskId: string; taskState: TaskRawStateEnum }) {
        return await put<{}>("/task/" + data.fromTaskId + "/changeTaskStatus", {
            taskState: data.taskState,
            toTaskId: data.toTaskId ? data.toTaskId : undefined,
        });
    }

    const onFilterBarUpdate = (filterOptions: FilterOption[]) => {
        const newFilterData = transformTaskFilterOptionsToBackend(filterOptions);
        setFilterData(withFilterOperators(newFilterData, filterOptions));
    };

    const onViewSaved = (viewId: string) => {
        const updatedView: SessionStorageSavedView = {
            page: FilterPage.Task,
            viewId: viewId,
            filterData: filterData,
            displayData: [],
        };
        setView(updatedView);
    };

    const onViewDeleted = (deletedViewId: string) => {
        const fallbackView = savedViewQuery.data?.find((x: SavedView) => x.isCustomerDefault || x.isSystemDefault);
        if (deletedViewId === currentViewId) {
            const updatedView: SessionStorageSavedView = {
                page: FilterPage.Task,
                viewId: fallbackView?.id === deletedViewId ? undefined : fallbackView?.id,
                filterData: filterData,
                displayData: [],
            };
            setView(updatedView);
        }
    };

    const toggleOverdueFilter = () => {
        if (filterData?.overdueTasks === true) {
            const { overdueTasks: _overdueTasks, ...rest } = filterData;
            setFilterData(rest);
        } else {
            setFilterData({ ...filterData, overdueTasks: true });
        }
    };

    const onChangeView = (view: SavedView) => {
        if (view.id !== currentViewId) {
            const updatedView: SessionStorageSavedView = {
                page: FilterPage.Task,
                viewId: view.id,
                filterData: view.filterData,
                displayData: view.displayData,
            };
            setView(updatedView);
        }
    };

    return {
        pinnedFilterIds,
        setPinnedFilterIds,
        isLoading,
        data,
        ready,
        inProgress,
        completed,
        changeTaskState,
        failedToChangeTaskState,
        year,
        setYear,
        refetchTasks,
        searchString,
        setSearchString,
        overdueTaskCount,
        inProgressTaskCount,
        completedTaskCount,
        filterData,
        setFilterData,
        hasFilterChanged,
        isSavedView,
        clearFilterData,
        onFilterBarUpdate,
        onViewSaved,
        onViewDeleted,
        onChangeView,
        currentViewId,
        savedViewQuery,
        previousYearsHaveOverdueTasks: previousYearsHaveOverdueTasks ?? false,
        toggleOverdueFilter,
        overdueFilterActive: filterData?.overdueTasks === true,
    };
}
